import * as React from "react";
import {useSelector} from "react-redux";
import {useFormikContext} from "formik";
import {Grid, Typography} from "@material-ui/core";
import {Alert} from "@material-ui/lab";
import {Label} from "../../formInputs";
import {ErrorCommon, ErrorHelper, SelectField, TextFieldCommon} from "../../formikFormInputs";
import {documentsSetting, fields, getDocument, tempEnrollmentDirectory} from "../../../pages/client/Kids/util";
import CommonUploader from "../../../pages/client/Kids/Uploader";
import {RootState} from "../../../reducers";
import {checkRole, noop} from "../../../utils/commonUtils";
import {AccountType, DocNameAndType} from "../../../constants";


const GiftorDocuments = (props: { enrollmentID?: string, isGWTGen2?: boolean }) => {
    const {enrollmentID, isGWTGen2} = props;
    const {
        errors,
        handleBlur,
        handleChange,
        setFieldTouched,
        setFieldValue,
        touched,
        values,
    } = useFormikContext<any>();

    const {user} = useSelector((state: RootState) => state.auth);
    const isNotIndividualOrMultiLife = !checkRole(user.role, [AccountType.Individual, AccountType.Multilife]);

    // visible on agent invite and giftor portal; all other giftor details tab etc admin/agent/imo disabled
    const disabled = isGWTGen2 ? false : isNotIndividualOrMultiLife;

    const formNames = fields;
    const verificationDocOptions = [
        {key: "drivingLicence", name: "Drivers License"},
        {key: "passport", name: "Passport"},
    ];
    const docNameAndTypeArr = Object.keys(DocNameAndType);
    const allowedDocs = ["Tax Returns", "W2’s", "1099’s"];
    const taxDocumentsOptions = docNameAndTypeArr
            .filter((item: string) => allowedDocs.includes(DocNameAndType[item].docName))
            .map(item => ({key: item, name: `${item} (GEN 1 GWT)`,}));

    const identityTypeFields = [
        {
            className: "mt20 mb20",
            component: SelectField,
            id: formNames.documentType,
            isRequired: !isGWTGen2,
            label: <Label label={"Verification Document Type"} required={!isGWTGen2}/>,
            name: formNames.documentType,
            onSelectChange: noop,
            options: verificationDocOptions,
            placeholder: "Verification Document Type",
        },
    ];
    const taxTypeFields = [
        {
            className: "mt20 mb20",
            component: SelectField,
            label: <Label label={"Verification Document Type"} required={!isGWTGen2}/>,
            name: formNames.taxDocumentType,
            onSelectChange: noop,
            options: taxDocumentsOptions,
            placeholder: "Verification Document Type",
        },
    ];

    const documentTypeValue = values[formNames.documentType];
    const taxDocumentTypeValue = values[formNames.taxDocumentType];

    const getUploadedDocument = getDocument(formNames.identityDocs, values);
    const geTaxDocuments = getDocument(formNames.taxDocument, values);

    const identityFrontDocs = getUploadedDocument(values[formNames.documentType], "front");
    const identityBackDocs = getUploadedDocument(values[formNames.documentType], "back");

    const taxFirstYearDocs = geTaxDocuments(values[formNames.taxDocumentType], "firstYear");
    const taxSecondYearDocs = geTaxDocuments(values[formNames.taxDocumentType], "secondYear");

    const fileRemover = (documentsName: string) => {
        return (docType, side) => {
            const removeFile = values[documentsName].filter((doc) => {
                return (doc.documentType != docType && doc.variant != side);
            });

            // TODO change this to include operation delete file ~~~ in simple words - delete from database and from maybe s3
            setFieldValue(documentsName, removeFile);
        };
    };
    const removeFileIdentity = fileRemover(formNames.identityDocs);
    const removeFileTaxDocument = fileRemover(formNames.taxDocument);

    // needed while saving/updating without it won't get inside document update function
    const formTypeDocument = {formType: "documents"};

    const customSetField = (key: string, value: any) => {
        values[key] = value;
        setFieldValue(key, value);
    };

    return (
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="body1" component="strong" className="mt20 mb15 floatLeft w100">
                        Provide Giftor Identity Documents
                        {!isGWTGen2 && <sup className="colorRed">*</sup>}
                    </Typography>
                    {
                        identityTypeFields.map((field, index) => {
                            return (
                                    <React.Fragment key={index}>
                                        <Grid item xs={12} sm={12} md={12} key={index}>
                                            <TextFieldCommon
                                                    {...field}
                                                    values={values}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                            />
                                            <ErrorCommon errors={errors} name={field.name} touched={touched}/>
                                        </Grid>
                                        <ErrorHelper errors={errors} name={formNames.identityDocs}/>
                                    </React.Fragment>
                            );
                        })
                    }
                    <div className="content">
                        {
                                !!documentTypeValue &&
                                <>
                                    <Typography variant="subtitle1" className="floatLeft w100" component="strong">
                                        <Label
                                                label={documentsSetting[values[formNames.documentType]].label}
                                                required={!isGWTGen2}
                                        />
                                    </Typography>
                                    <Typography variant={"inherit"}>
                                        {
                                            documentTypeValue === 'drivingLicence' ?
                                                    <span>
                                                        Front and back of a current, <strong>non-expired</strong> drivers license <br/>
                                                        We can not accept a digital ID card. Only upload a scanned copy of your driver's license.
                                                    </span> :
                                                    <span>
                                                        Photo and signature page of a current <strong>non-expired</strong> passport to verify your identity
                                                    </span>
                                        }
                                    </Typography>
                                </>
                        }
                        <Grid container spacing={2} className="mt15">
                            {/* for front view  */}
                            {
                                    !!documentTypeValue &&
                                    <Grid item xs={12} sm={6}>
                                        <CommonUploader
                                                disabled={disabled}
                                                dir={enrollmentID || tempEnrollmentDirectory}
                                                docType={`${documentsSetting[values[formNames.documentType]].label} Front`}
                                                onFinish={async (publicUrl: string, name: string) => {
                                                    setFieldTouched(formNames.documentType);
                                                    customSetField(formNames.identityDocs, [...values[formNames.identityDocs], {
                                                        document: values[formNames.documentType],
                                                        identityFrontPhoto: name,
                                                        operation: "Uploaded",
                                                        path: name,
                                                        variant: "front",
                                                        ...documentsSetting[values[formNames.documentType]].front.additionalDetials,
                                                        ...formTypeDocument,
                                                    }]);
                                                }}
                                                preview={{
                                                    file: {
                                                        name: identityFrontDocs && identityFrontDocs.path,
                                                        viewTitle: documentsSetting[values[formNames.documentType]].label,
                                                        size: 12,
                                                    },
                                                    onDelete: () => {
                                                        removeFileIdentity(values[formNames.documentType], "front");
                                                    },
                                                }}
                                        />
                                    </Grid>
                            }
                            {/* for front view  */}

                            {/* for back view  */}
                            {
                                    !!documentTypeValue &&
                                    <Grid item xs={12} sm={6}>
                                        <CommonUploader
                                                disabled={disabled}
                                                dir={enrollmentID || tempEnrollmentDirectory}
                                                docType={`${documentsSetting[values[formNames.documentType]].label} Back`}
                                                onFinish={async (publicUrl: string, name: string) => {
                                                    setFieldTouched(formNames.documentType);
                                                    customSetField(formNames.identityDocs, [...values[formNames.identityDocs], {
                                                        backPath: name,
                                                        document: values[formNames.documentType],
                                                        identityBackPhoto: name,
                                                        operation: "Uploaded",
                                                        path: name,
                                                        variant: "back",
                                                        ...documentsSetting[values[formNames.documentType]].back.additionalDetials,
                                                        ...formTypeDocument,
                                                    }]);
                                                }}
                                                preview={{
                                                    file: {
                                                        name: identityBackDocs && identityBackDocs.path,
                                                        viewTitle: documentsSetting[values[formNames.documentType]].label,
                                                        size: 12,
                                                    },
                                                    onDelete: () => {
                                                        removeFileIdentity(values[formNames.documentType], "back");
                                                    },
                                                }}
                                        />
                                    </Grid>
                            }
                            {/* for back view  */}
                        </Grid>
                    </div>

                    {/* Tax Documents document sections */}
                    <Typography variant="body1" component="strong" className="floatLeft w100 mb20">
                        Provide Giftor Tax Documents
                        {!isGWTGen2 && <sup className="colorRed">*</sup>}
                    </Typography>
                    {
                        taxTypeFields.map((field, index) => {
                            return (
                                    <React.Fragment key={index}>
                                        <Grid item xs={12} sm={12} md={12} key={index}>
                                            <TextFieldCommon
                                                    {...field}
                                                    values={values}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                            />
                                            <ErrorCommon errors={errors} name={field.name} touched={touched}/>
                                        </Grid>
                                        <ErrorHelper errors={errors} name={formNames.taxDocument}/>
                                    </React.Fragment>
                            );
                        })
                    }
                    {
                            isGWTGen2 &&
                            <Alert variant="outlined" severity="info" className="rainbow mt25 mb15">
                                <Typography variant="body2">
                                    Only upload one form of Tax Document: W2 or Tax Returns or 1099. Please do not
                                    upload placeholder documents or references to other ilia accounts. We cannot access
                                    previously uploaded documents from the Carrier or other ilia accounts.
                                </Typography>
                            </Alert>
                    }
                    <div className="content">
                        <Grid container spacing={2} className="mt15">
                            {/* first year view  */}
                            {
                                    !!taxDocumentTypeValue &&
                                    <Grid item xs={12} sm={6}>
                                        <CommonUploader
                                                disabled={disabled}
                                                dir={enrollmentID || tempEnrollmentDirectory}
                                                docType={`Year 1`}
                                                onFinish={async (publicUrl: string, name: string) => {
                                                    setFieldTouched(formNames.taxDocumentType);
                                                    customSetField(formNames.taxDocument, [...values[formNames.taxDocument], {
                                                        document: values[formNames.taxDocumentType],
                                                        operation: "Uploaded",
                                                        path: name,
                                                        taxDocumentYear1: name,
                                                        variant: "firstYear",
                                                        ...documentsSetting[values[formNames.taxDocumentType]].firstYear.additionalDetials,
                                                        ...formTypeDocument,

                                                    }]);
                                                }}
                                                preview={{
                                                    file: {
                                                        name: taxFirstYearDocs && taxFirstYearDocs.path,
                                                        viewTitle: documentsSetting[values[formNames.taxDocumentType]] && documentsSetting[values[formNames.taxDocumentType]].label,
                                                        size: 12,
                                                    },
                                                    onDelete: () => {
                                                        removeFileTaxDocument(values[formNames.taxDocumentType], "firstYear");
                                                    },
                                                }}
                                        />
                                    </Grid>
                            }
                            {/* first year view  */}

                            {/* second year view  */}
                            {
                                    !!taxDocumentTypeValue &&
                                    <Grid item xs={12} sm={6}>
                                        <CommonUploader
                                                disabled={disabled}
                                                dir={enrollmentID || tempEnrollmentDirectory}
                                                docType={`Year 2`}
                                                onFinish={async (publicUrl: string, name: string) => {
                                                    setFieldTouched(formNames.taxDocumentType);
                                                    customSetField(formNames.taxDocument, [...values[formNames.taxDocument], {
                                                        document: values[formNames.taxDocumentType],
                                                        operation: "Uploaded",
                                                        path: name,
                                                        taxDocumentYear2: name,
                                                        variant: "secondYear",
                                                        ...documentsSetting[values[formNames.taxDocumentType]].secondYear.additionalDetials,
                                                        ...formTypeDocument,
                                                    }]);
                                                }}
                                                preview={{
                                                    file: {
                                                        name: taxSecondYearDocs && taxSecondYearDocs.path,
                                                        viewTitle: documentsSetting[values[formNames.taxDocumentType]] && documentsSetting[values[formNames.taxDocumentType]].label,
                                                        size: 12,
                                                    },
                                                    onDelete: () => {
                                                        removeFileTaxDocument(values[formNames.taxDocumentType], "secondYear");
                                                    },
                                                }}
                                        />
                                    </Grid>
                            }
                            {/* second year view  */}
                        </Grid>
                    </div>
                    {/* Tax Documents document sections */}
                </Grid>
            </Grid>
    );
};

export default GiftorDocuments;